import { GTFonts } from '../theme';
import React, {
  useCallback,
  useMemo,
  useState,
  useContext,
  useEffect,
  useRef,
} from 'react';
import {
  View,
  Text,
  StyleSheet,
  Image,
  Platform,
  Dimensions,
  TouchableOpacity,
  Keyboard,
} from 'react-native';
import { IconButton, Tooltip } from 'react-native-paper';
import {
  BottomSheetModal,
  BottomSheetScrollView,
  BottomSheetTextInput,
} from '@gorhom/bottom-sheet';
import CustomBottomSheet from './Reusables/bottomSheet/CustomBottomSheet';
import Button from './Reusables/Button';
import QuantityControls from './Reusables/QuantityControls';
import { useDispatch } from 'react-redux';
import {
  addToCart,
  addToCartQuantity,
  getTotals,
  removeCartItem,
} from '../redux/cartSlice';
import { useNavigation } from '@react-navigation/native';
import { useAppSelector } from '../redux/helper';
import { RootState } from '../redux/types';
import { selectCartItems, selectCartOrderData } from '../redux/cartSlice';
import { selectQuantityButtons } from '../redux/SettingsSlice';
import {
  formatNumberDecimal,
  formatWithCommas,
  forceHttps,
  replaceCurrencyString,
  fs,
  s,
  iconSize,
  v,
  borderWidth,
  isNumericEffectivelyZero,
} from '../utils/libs';
import { getApiMessage } from '../utils/apiError';
import ErrorContext from '../Context/ErrorContext';
import { AppSnackbar } from '../services/AppSnackbar';
import DeviceInfo from 'react-native-device-info';
import { useTheme } from '../theme';
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import useAuth from '../hooks/useAuth';
import { updateEmployeeProductFavourite } from '../api/products';
import { mergeProductFields } from '../redux/productSlice';
import { isEmployeeOwnerRole } from '../utils/roles';
import { showPaidLineCannotRemove } from '../utils/paidLineFloor';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import Ionicons from '@react-native-vector-icons/ionicons/static';
import FontAwesome6 from '@react-native-vector-icons/fontawesome6/static';
import {
  composeProductVariantLabel,
  getProductDisplayName,
} from '../utils/productDisplayName';

const isTablet = DeviceInfo.isTablet();
const { width: screenWidth } = Dimensions.get('window');

export type ProductDetailCartBridgeItem = {
  productID: string;
  variantId?: string | null;
  Quantity?: number;
  mass?: number;
  bp_order_paid_quantity?: number;
  bp_order_paid_mass?: number;
};

/** When provided, cart ops use this bridge instead of Redux (demo POS). */
export type ProductDetailCartBridge = {
  cartItems: ProductDetailCartBridgeItem[];
  onAdd: (product: any) => void;
  onSetQuantity: (args: {
    productID: string;
    variantId?: string | null;
    quantity: number;
  }) => void;
  onRemove: (args: {
    productID: string;
    variantId?: string | null;
  }) => void;
};

interface ProductDetailBottomSheetProps {
  bottomSheetRef: React.RefObject<BottomSheetModal>;
  product: any | null;
  handleSubproductQuantity: (product: any) => void;
  cartBridge?: ProductDetailCartBridge;
}

const getCartIdentityKey = (item: {
  productID?: string | null;
  variantId?: string | null;
}): string => `${item?.productID ?? ''}::${item?.variantId ?? ''}`;

const ProductDetailBottomSheet: React.FC<ProductDetailBottomSheetProps> = ({
  bottomSheetRef,
  product,
  handleSubproductQuantity,
  cartBridge,
}) => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);
  const dispatch = useDispatch();
  const { handleError } = useContext(ErrorContext);
  const {
    textInputError,
    setTextInputError,
    quantityInputError,
    setQuantityInputError,
  } = useContext(ErrorContext);

  const reduxCartItems = useAppSelector(selectCartItems);
  const reduxCartOrderData = useAppSelector(selectCartOrderData);
  const reduxPendingPreviousOrder = useAppSelector(
    state => state?.orders?.pending?.previousDocs,
  );
  const reduxUpdatingCartitems = useAppSelector(
    state => state?.cart?.updatingCartitems,
  );
  const cartItems = (cartBridge?.cartItems ?? reduxCartItems) as any[];
  const cartOrderData = cartBridge
    ? ({ orderType: '' } as any)
    : reduxCartOrderData;
  const pendingPreviousOrder = cartBridge ? [] : reduxPendingPreviousOrder;
  const updatingCartitems = cartBridge ? false : reduxUpdatingCartitems;

  const applyAddToCart = useCallback(
    (payload: any) => {
      const orderSnapshot = pendingPreviousOrder?.find(
        (doc: any) =>
          doc.productID === payload.productID &&
          (doc.variantId ?? null) === (payload.variantId ?? null),
      );
      const enriched = orderSnapshot
        ? {
            ...payload,
            bp_order_paid_quantity: orderSnapshot.bp_order_paid_quantity,
            bp_order_paid_mass: orderSnapshot.bp_order_paid_mass,
            bp_order_completed: orderSnapshot.bp_order_completed,
            bp_sale_original_quantity: orderSnapshot.bp_sale_original_quantity,
          }
        : payload;
      if (cartBridge) {
        cartBridge.onAdd(enriched);
        return;
      }
      dispatch(addToCart(enriched));
      dispatch(getTotals());
    },
    [cartBridge, dispatch, pendingPreviousOrder],
  );

  const applyRemoveCartItem = useCallback(
    (args: { productID: string; variantId?: string | null }) => {
      if (cartBridge) {
        cartBridge.onRemove(args);
        return;
      }
      dispatch(
        removeCartItem({
          productID: args.productID,
          variantId: args.variantId ?? undefined,
        }),
      );
      dispatch(getTotals());
    },
    [cartBridge, dispatch],
  );

  const applySetCartQuantity = useCallback(
    (args: {
      productID: string;
      variantId?: string | null;
      quantity: number;
      product?: any;
    }) => {
      if (cartBridge) {
        cartBridge.onSetQuantity(args);
        return;
      }
      dispatch(
        addToCartQuantity({
          productID: args.productID,
          variantId: args.variantId ?? undefined,
          quantity: args.quantity,
          product: args.product,
        }),
      );
      dispatch(getTotals());
    },
    [cartBridge, dispatch],
  );
  const currency = useAppSelector(
    state =>
      state.location.currentLocation?.bp_location_settings
        ?.bp_location_currency || 'KES',
  );
  const currencyText = useMemo(
    () => replaceCurrencyString('Ksh', currency),
    [currency],
  );

  const [massValue, setMassValue] = useState('');
  const [variantMassText, setVariantMassText] = useState<
    Record<string, string>
  >({});
  const [isReady, setIsReady] = useState(false);
  const [sheetIndex, setSheetIndex] = useState<number>(0);
  const rafIdRef = useRef<number | null>(null);
  const expandedByInputRef = useRef<boolean>(false);
  const navigation = useNavigation<any>();
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const employeedata = useAppSelector(
    (state: RootState) => state.auth.inApp.account.employeeInfo,
  );
  const isOwner = isEmployeeOwnerRole(employeedata?.bp_employee_role);
  const [isFavourite, setIsFavourite] = useState<boolean>(false);
  const [isFavLoading, setIsFavLoading] = useState<boolean>(false);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);
  const currentOrderType = String(cartOrderData?.orderType || '').toLowerCase();
  const originalOrderProductIdSet = useMemo(
    () => new Set(pendingPreviousOrder?.map(d => getCartIdentityKey(d)) ?? []),
    [pendingPreviousOrder],
  );

  const hasVariants = useMemo(
    () => Boolean(product?.hasVariants ?? product?.bp_product_has_variants),
    [product],
  );
  const snapPoints = useMemo(
    () => (hasVariants ? ['65%', '85%'] : ['55%', '88%']),
    [hasVariants],
  );
  const variants = useMemo(() => {
    if (Array.isArray(product?.variants)) return product.variants;
    if (Array.isArray(product?.bp_product_variants)) return product.bp_product_variants;
    return [];
  }, [product]);
  const selectedVariant = useMemo(() => {
    if (!hasVariants || !variants.length) return null;
    return (
      variants.find((variant: any) => variant?._id === selectedVariantId) ||
      variants[0] ||
      null
    );
  }, [hasVariants, variants, selectedVariantId]);

  useEffect(() => {
    if (!hasVariants || !variants.length) {
      setSelectedVariantId(null);
      return;
    }
    setSelectedVariantId((prev: string | null) => {
      if (prev && variants.some((variant: any) => variant?._id === prev)) return prev;
      return variants[0]?._id ?? null;
    });
  }, [hasVariants, variants]);

  // Settings-driven quantity button colors
  const quantityButtons = useAppSelector(selectQuantityButtons) as any;

  type ColorKey = keyof typeof theme.colors;
  const resolveColor = (key: string | undefined, fallback: ColorKey) => {
    const k = (key || '') as ColorKey;
    return theme.colors[k] ?? theme.colors[fallback];
  };
  const resolveLightForMain = (
    mainKey: string | undefined,
    fallback: ColorKey,
  ) => {
    const main = (mainKey || '') as ColorKey;
    const lightKey = (main + 'Light') as ColorKey;
    if (theme.colors[lightKey]) return theme.colors[lightKey];
    return theme.colors[fallback];
  };

  // Get cart item for this product
  const cartItem = useMemo(() => {
    if (!product) return null;
    return cartItems?.find(
      item =>
        item.productID === product.productID &&
        (item.variantId ?? null) === (hasVariants ? (selectedVariant?._id ?? null) : null),
    );
  }, [cartItems, product, hasVariants, selectedVariant]);

  // Initialize mass value when product changes or cart updates
  useEffect(() => {
    if (product?.price_type === 'unit' && cartItem?.mass) {
      setMassValue(cartItem.mass.toString());
    } else if (product?.price_type === 'unit') {
      setMassValue('');
    }
  }, [product, cartItem]);

  // Keep per-variant mass draft text in sync with cart (unit + variants)
  useEffect(() => {
    if (!hasVariants || product?.price_type !== 'unit' || !product) return;
    setVariantMassText(prev => {
      const next: Record<string, string> = { ...prev };
      let changed = false;
      for (const variant of variants) {
        const id = variant?._id;
        if (!id) continue;
        const cartLine = cartItems?.find(
          (item: any) =>
            item.productID === product.productID && item.variantId === id,
        );
        const cartMassNum =
          cartLine?.mass != null ? Number(cartLine.mass) : 0;
        const massStr = cartMassNum > 0 ? String(cartLine.mass) : '';
        const draftStr = next[id] ?? '';
        const draftMassNum =
          draftStr !== '' && !isNaN(Number(draftStr))
            ? Number(draftStr)
            : null;
        // Sync from cart only when the draft is empty (initialize) or the
        // numeric value differs from the cart. Comparing as strings would
        // clobber an in-progress decimal draft like "1." with the cart's "1".
        const numericallyEqual =
          draftMassNum !== null && draftMassNum === cartMassNum;
        if (!numericallyEqual && next[id] !== massStr) {
          next[id] = massStr;
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [hasVariants, product, variants, cartItems]);

  // Initialize favourite state from product when it changes
  useEffect(() => {
    if (!product) return;
    const fav = Boolean(product?.isFavourite ?? false);
    setIsFavourite(fav);
  }, [product]);

  // Keyboard handling for native
  useEffect(() => {
    const eventName =
      Platform.OS === 'ios' ? 'keyboardWillHide' : 'keyboardDidHide';
    const sub = Keyboard.addListener(eventName, () => {
      if (expandedByInputRef.current) {
        bottomSheetRef.current?.snapToIndex?.(0);
        expandedByInputRef.current = false;
      }
    });
    return () => sub.remove();
  }, [bottomSheetRef]);

  const handleInputFocus = useCallback(() => {
    if (sheetIndex === 0) {
      expandedByInputRef.current = true;
      bottomSheetRef.current?.expand?.();
    }
  }, [sheetIndex, bottomSheetRef]);

  const handleInputBlur = useCallback(() => {
    if (expandedByInputRef.current) {
      bottomSheetRef.current?.snapToIndex?.(0);
      expandedByInputRef.current = false;
    }
  }, [bottomSheetRef]);

  const handleProductImageEditPress = useCallback(() => {
    if (!product?.productID) return;
    bottomSheetRef.current?.dismiss();
    navigation.navigate('EmployeeAddProductImage', {
      productId: product.productID,
    });
  }, [product?.productID, bottomSheetRef, navigation]);

  const onToggleFavourite = useCallback(async () => {
    if (!product || isFavLoading) return;
    const next = !isFavourite;

    setIsFavLoading(true);
    setIsFavourite(next);
    dispatch(
      mergeProductFields({
        productId: product.productID,
        updates: { bp_is_favourite: next },
      }),
    );

    try {
      const res = await updateEmployeeProductFavourite(
        axiosPrivate,
        product.productID,
        next,
        auth,
      );
      const ok =
        res?.data?.status === 200 ||
        res?.data?.status === 'success' ||
        res?.status === 200;
      if (!ok) {
        setIsFavourite(!next);
        dispatch(
          mergeProductFields({
            productId: product.productID,
            updates: { bp_is_favourite: !next },
          }),
        );
        AppSnackbar.show(
          String(res?.data?.message || 'Failed to update favourite'),
          'error',
          { durationMs: 4500 },
        );
        return;
      }
      AppSnackbar.show(
        next ? 'Added to favourites' : 'Removed from favourites',
        'success',
      );
    } catch (e: any) {
      setIsFavourite(!next);
      dispatch(
        mergeProductFields({
          productId: product.productID,
          updates: { bp_is_favourite: !next },
        }),
      );
      AppSnackbar.show(
        getApiMessage(e, 'Failed to update favourite'),
        'error',
        {
          durationMs: 4500,
        },
      );
    } finally {
      setIsFavLoading(false);
    }
  }, [product, isFavLoading, isFavourite, axiosPrivate, auth, dispatch]);

  const handleSheetChanges = useCallback((index: number) => {
    setSheetIndex(index);
    if (index === -1) {
      setMassValue('');
      setVariantMassText({});
      setIsReady(false);
      if (rafIdRef.current != null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }
      return;
    }
    if (index >= 0) {
      rafIdRef.current = requestAnimationFrame(() => {
        setIsReady(true);
        rafIdRef.current = null;
      });
    }
  }, []);

  const validateQuantityError = useCallback(
    (
      nextQuantity: number,
      productId: string,
      options?: { variantId?: string | null; variantStock?: number },
    ) => {
      if (!product) return;
      const variantId = options?.variantId ?? null;
      const errorKey =
        variantId != null
          ? getCartIdentityKey({ productID: productId, variantId })
          : productId;
      const found = cartItems?.find(
        (item: any) =>
          item.productID === productId &&
          (item.variantId ?? null) === (variantId ?? null),
      );
      const effectiveStock =
        options?.variantStock ?? Number(product.quantity ?? 0);

      if (
        updatingCartitems &&
        nextQuantity < (Number(found?.bp_order_paid_quantity) ?? 0)
      ) {
        setQuantityInputError(prev => ({ ...prev, [errorKey]: true }));
      } else if (nextQuantity <= effectiveStock) {
        setQuantityInputError(prev => ({ ...prev, [errorKey]: false }));
      }
    },
    [cartItems, updatingCartitems, product, setQuantityInputError],
  );

  const onAdd = useCallback(() => {
    if (!product) return;
    applyAddToCart(product);
    if (product.price_type === 'item') {
      const newQuantity = (cartItem?.Quantity ?? 0) + 1;
      validateQuantityError(newQuantity, product.productID);
    }
  }, [product, applyAddToCart, cartItem, validateQuantityError]);

  const onSub = useCallback(() => {
    if (!product || !cartItems || !cartOrderData) return;

    const paidQty = Number(cartItem?.bp_order_paid_quantity ?? 0);
    const currentQty = cartItem?.Quantity ?? 0;
    if (paidQty > 0 && currentQty <= paidQty) {
      showPaidLineCannotRemove();
      return;
    }

    handleSubproductQuantity(product);
  }, [product, cartItems, cartOrderData, cartItem, handleSubproductQuantity]);

  const handleMassInputChange = useCallback(
    (input: string) => {
      if (!product) return;

      const productID = product.productID;
      let sanitizedInput = input.replace(/^0+(?=\d)/, '');
      if (sanitizedInput.startsWith('.')) {
        sanitizedInput = `0${sanitizedInput}`;
      }

      const paidMass = Number(cartItem?.bp_order_paid_mass ?? 0);

      if (sanitizedInput === '') {
        // Mirror Product.web: let the user clear the field even on a paid
        // line. Just flag the error; restore-to-paid happens on blur.
        setMassValue('');
        if (paidMass > 0) {
          setTextInputError(prev => ({ ...prev, [productID]: true }));
          return;
        }
        // Unpaid pending lines should remove on clear (same as item qty).
        // Paid lines already returned above; blur restores paid floor.
        // if (!updatingCartitems) {
        applyRemoveCartItem({ productID });
        // }
        return;
      }

      if (!/^\d*\.?\d*$/.test(sanitizedInput)) return;

      setMassValue(sanitizedInput);

      if (paidMass > 0 && Number(sanitizedInput) < paidMass) {
        setTextInputError(prev => ({ ...prev, [productID]: true }));
      }

      if (sanitizedInput.endsWith('.')) return;

      const newQuantity = Number.parseFloat(sanitizedInput);
      if (!Number.isFinite(newQuantity) || newQuantity <= 0) return;

      if (paidMass > 0 && newQuantity < paidMass) {
        // Mirror Product.web: let the user type freely while below paid,
        // just flag the error. Restore-to-paid happens on blur.
        applyAddToCart({ ...product, mass: newQuantity });
        setTextInputError(prev => ({ ...prev, [productID]: true }));
        return;
      }

      if (updatingCartitems && cartOrderData?.order?.length) {
        const originalProduct = cartOrderData?.order?.find(
          (op: any) => op?.bp_product_information?._id === productID,
        );
        if (originalProduct?.bp_product_information) {
          const productInfo = originalProduct.bp_product_information;
          const totalProductQuantity =
            productInfo.bp_product_price_type === 'unit'
              ? Number(productInfo.bp_product_quantity) +
              Number(originalProduct?.bp_order_paid_mass ?? 0)
              : Number(productInfo.bp_product_quantity) +
              Number(originalProduct?.bp_order_paid_quantity ?? 0);

          if (newQuantity <= Number(totalProductQuantity)) {
            setTextInputError(prevErrors => ({
              ...prevErrors,
              [productID]: false,
            }));
            applyAddToCart({ ...product, mass: newQuantity });
            return;
          } else {
            handleError(
              `Value is greater than available stock of ${formatNumberDecimal(
                totalProductQuantity,
              )} ${product.unitofmass}`,
              'error',
              true,
            );
            setTextInputError(prevErrors => ({
              ...prevErrors,
              [productID]: true,
            }));
            return;
          }
        }
      }

      if (newQuantity <= Number(product.quantity)) {
        setTextInputError(prevErrors => ({
          ...prevErrors,
          [productID]: false,
        }));
        applyAddToCart({ ...product, mass: newQuantity });
      } else {
        handleError(
          `Value is greater than available stock of ${formatNumberDecimal(
            product.quantity,
          )} ${product.unitofmass}`,
          'error',
          true,
        );
        setTextInputError(prevErrors => ({
          ...prevErrors,
          [productID]: true,
        }));
      }
    },
    [
      product,
      cartItem,
      updatingCartitems,
      cartOrderData,
      handleError,
      setTextInputError,
      applyAddToCart,
      applyRemoveCartItem,
    ],
  );

  const handleQuantityInputChange = useCallback(
    (input: string) => {
      if (!product) return;

      const productID = product.productID;
      let sanitizedInput = parseInt(input.replace(/^0+(?=\d)/, '')) || 0;

      const paidQty = Number(cartItem?.bp_order_paid_quantity ?? 0);
      if (paidQty > 0 && sanitizedInput < paidQty) {
        // Mirror product list: let the user type freely while below paid,
        // just flag the error. Restore-to-paid happens on blur.
        applySetCartQuantity({
          productID,
          quantity: sanitizedInput,
          product,
        });
        setQuantityInputError(prevErrors => ({
          ...prevErrors,
          [productID]: true,
        }));
        return;
      }

      if (sanitizedInput <= 0) {
        applyRemoveCartItem({ productID });
        return;
      }

      if (
        cartItem &&
        Number(sanitizedInput) === Number(cartItem.Quantity ?? 0)
      ) {
        return;
      }

      if (updatingCartitems && cartOrderData?.order?.length) {
        const originalProduct = cartOrderData?.order?.find(
          (op: any) => op?.bp_product_information?._id === productID,
        );
        if (originalProduct?.bp_product_information) {
          const unpaidQuantity =
            Number(originalProduct?.bp_order_quantity ?? 0) -
            Number(originalProduct?.bp_order_paid_quantity ?? 0);
          const totalProductQuantity =
            Number(product.quantity) +
            Number(unpaidQuantity) +
            Number(originalProduct?.bp_order_paid_quantity ?? 0);

          if (Number(sanitizedInput) <= Number(totalProductQuantity)) {
            applySetCartQuantity({
              productID,
              quantity: Number(sanitizedInput),
              product,
            });
            setQuantityInputError(prevErrors => ({
              ...prevErrors,
              [productID]: false,
            }));
            return;
          }

          setQuantityInputError(prevErrors => ({
            ...prevErrors,
            [productID]: true,
          }));
          handleError(
            `Value is greater than available stock of ${formatNumberDecimal(
              totalProductQuantity,
            )}`,
            'error',
            true,
          );
          return;
        }
      }

      if (Number(sanitizedInput) <= Number(product.quantity)) {
        applySetCartQuantity({
          productID,
          quantity: Number(sanitizedInput),
          product,
        });
        setQuantityInputError(prevErrors => ({
          ...prevErrors,
          [productID]: false,
        }));
      } else {
        handleError(
          `Value is greater than available stock of ${formatNumberDecimal(
            product.quantity,
          )}`,
          'error',
          true,
        );
        setQuantityInputError(prevErrors => ({
          ...prevErrors,
          [productID]: true,
        }));
      }
    },
    [
      product,
      cartItem,
      updatingCartitems,
      cartOrderData,
      applyRemoveCartItem,
      applySetCartQuantity,
      handleError,
      setQuantityInputError,
    ],
  );

  const activePrice = hasVariants
    ? Number(selectedVariant?.bp_variant_selling_price ?? 0)
    : Number(product?.price || 0);
  const activeQuantity = hasVariants
    ? Number(selectedVariant?.bp_variant_quantity ?? 0)
    : Number(product?.quantity || 0);
  const activeMinimumStock = hasVariants
    ? Number(selectedVariant?.bp_variant_minimum_stock_level ?? 0)
    : Number(product?.msl || 0);
  const activeImage = hasVariants
    ? selectedVariant?.bp_variant_image || product?.image
    : product?.image;
  const selectedVariantLabel = hasVariants
    ? selectedVariant?.bp_variant_display_name ||
    Object.values(selectedVariant?.bp_variant_attributes || {})
      .filter(Boolean)
      .join(' / ') ||
    ''
    : null;
  const productName = hasVariants
    ? composeProductVariantLabel(product?.itemname, selectedVariantLabel)
    : getProductDisplayName(product);

  // Exception-only: healthy stock stays silent. Overlay on the image was
  // unreadable on light photos and redundant with the Inventory card.
  const stockStatus = (() => {
    if (activeQuantity <= 0) {
      return {
        label: 'Out of stock',
        color: theme.colors.error,
        backgroundColor: theme.colors.errorLight,
        icon: 'close-circle' as const,
      };
    }
    if (activeMinimumStock > 0 && activeQuantity <= activeMinimumStock) {
      return {
        label: 'Low stock',
        color: theme.colors.warningDark,
        backgroundColor: `${theme.colors.warningDark}1A`,
        icon: 'alert-circle' as const,
      };
    }
    return null;
  })();

  const buildVariantCartPayload = useCallback((variant: any) => {
    if (!product || !variant) return null;
    const attributeValues = Object.values(
      variant.bp_variant_attributes || {},
    ).filter(Boolean);
    const variantDisplayName = composeProductVariantLabel(
      product.itemname,
      variant.bp_variant_display_name ||
      (attributeValues.length ? attributeValues.join(' / ') : ''),
    );
    return {
      ...product,
      variantId: variant._id,
      itemname: variantDisplayName,
      price: Number(variant.bp_variant_selling_price ?? 0),
      quantity: Number(variant.bp_variant_quantity ?? 0),
      image: variant.bp_variant_image || product.image,
      msl: Number(variant.bp_variant_minimum_stock_level ?? 0),
      bp_variant_attributes: variant.bp_variant_attributes || {},
      bp_product_parent_name: product.itemname,
    };
  }, [product]);

  const buildSelectedVariantCartPayload = useCallback(() => {
    return buildVariantCartPayload(selectedVariant);
  }, [buildVariantCartPayload, selectedVariant]);

  const onAddVariant = useCallback((variant: any) => {
    const payload = buildVariantCartPayload(variant);
    if (!payload) return;
    if (Number(payload.quantity ?? 0) <= 0) {
      AppSnackbar.show(`${payload.itemname} is out of stock`, 'warning', {
        durationMs: 4500,
      });
      return;
    }
    const variantCartItem = cartItems?.find(
      (item: any) =>
        item.productID === product?.productID &&
        item.variantId === variant?._id,
    );
    if (payload.price_type === 'unit') {
      const currentMass = Number(variantCartItem?.mass ?? 0);
      const nextMass = Math.min(
        currentMass + 1,
        Number(payload.quantity ?? currentMass + 1),
      );
      if (nextMass <= currentMass) {
        AppSnackbar.show(`${payload.itemname} is out of stock`, 'warning', {
          durationMs: 4500,
        });
        return;
      }
      applyAddToCart({ ...payload, mass: nextMass });
      return;
    }
    applyAddToCart(payload);
    if (payload.price_type === 'item') {
      const newQuantity = Number(variantCartItem?.Quantity ?? 0) + 1;
      validateQuantityError(newQuantity, product?.productID ?? '', {
        variantId: variant?._id ?? null,
        variantStock: Number(variant?.bp_variant_quantity ?? 0),
      });
    }
  }, [
    buildVariantCartPayload,
    cartItems,
    product,
    applyAddToCart,
    validateQuantityError,
  ]);

  const onSubVariant = useCallback((variant: any) => {
    const payload = buildVariantCartPayload(variant);
    if (!payload) return;
    const variantCartItem = cartItems?.find(
      (item: any) =>
        item.productID === product?.productID &&
        item.variantId === variant?._id,
    );
    if (!variantCartItem) return;

    if (payload.price_type === 'unit') {
      const paidMass = Number(variantCartItem?.bp_order_paid_mass ?? 0);
      const currentMass = Number(variantCartItem?.mass ?? 0);
      const nextMass = Math.max(0, currentMass - 1);
      if (paidMass > 0 && nextMass < paidMass) {
        showPaidLineCannotRemove();
        return;
      }
      if (nextMass <= 0) {
        applyRemoveCartItem({
          productID: payload.productID,
          variantId: payload.variantId,
        });
      } else {
        applyAddToCart({ ...payload, mass: nextMass });
      }
      return;
    }

    const paidQty = Number(variantCartItem?.bp_order_paid_quantity ?? 0);
    const currentQty = variantCartItem?.Quantity ?? 0;
    if (paidQty > 0 && currentQty <= paidQty) {
      showPaidLineCannotRemove();
      return;
    }
    handleSubproductQuantity(payload);
  }, [
    buildVariantCartPayload,
    cartItems,
    product,
    applyAddToCart,
    applyRemoveCartItem,
    handleSubproductQuantity,
  ]);

  const handleVariantMassChange = useCallback(
    (variant: any, input: string) => {
      if (!product || !variant?._id) return;

      const payload = buildVariantCartPayload(variant);
      if (!payload) return;

      const productID = product.productID;
      const variantId = variant._id;
      const errorKey = getCartIdentityKey({ productID, variantId });
      let sanitizedInput = input.replace(/^0+(?=\d)/, '');
      if (sanitizedInput.startsWith('.')) {
        sanitizedInput = `0${sanitizedInput}`;
      }
      const variantStock = Number(variant.bp_variant_quantity ?? 0);
      const variantCartItem = cartItems?.find(
        (item: any) =>
          item.productID === productID && item.variantId === variantId,
      );
      const paidMass = Number(variantCartItem?.bp_order_paid_mass ?? 0);

      const setVariantMass = (value: string) => {
        setVariantMassText(prev => ({ ...prev, [variantId]: value }));
      };

      if (sanitizedInput === '') {
        // Mirror Product.web: let the user clear the field even on a paid
        // line. Just flag the error; restore-to-paid happens on blur.
        setVariantMass('');
        if (paidMass > 0) {
          setTextInputError(prev => ({ ...prev, [errorKey]: true }));
          return;
        }
        // Unpaid pending lines should remove on clear (same as item qty).
        // Paid lines already returned above; blur restores paid floor.
        // if (!updatingCartitems) {
        applyRemoveCartItem({ productID, variantId });
        // }
        return;
      }

      if (!/^\d*\.?\d*$/.test(sanitizedInput)) return;

      setVariantMass(sanitizedInput);

      if (paidMass > 0 && Number(sanitizedInput) < paidMass) {
        setTextInputError(prev => ({ ...prev, [errorKey]: true }));
      }

      if (sanitizedInput.endsWith('.')) return;

      const newQuantity = Number.parseFloat(sanitizedInput);
      if (!Number.isFinite(newQuantity) || newQuantity <= 0) return;

      if (paidMass > 0 && newQuantity < paidMass) {
        // Mirror Product.web: let the user type freely while below paid,
        // just flag the error. Restore-to-paid happens on blur.
        applyAddToCart({ ...payload, mass: newQuantity });
        setTextInputError(prev => ({ ...prev, [errorKey]: true }));
        return;
      }

      if (updatingCartitems && cartOrderData?.order?.length) {
        const originalProduct = cartOrderData.order.find(
          (op: any) =>
            op?.bp_product_information?._id === productID &&
            (op?.bp_product_information?.bp_variant_id ??
              op?.bp_variant_id ??
              null) === variantId,
        );
        if (originalProduct?.bp_product_information) {
          const unpaidMass =
            Number(originalProduct?.bp_order_mass ?? 0) -
            Number(originalProduct?.bp_order_paid_mass ?? 0);
          const totalProductQuantity =
            variantStock +
            Number(unpaidMass) +
            Number(originalProduct?.bp_order_paid_mass ?? 0);

          if (newQuantity <= Number(totalProductQuantity)) {
            setTextInputError(prev => ({ ...prev, [errorKey]: false }));
            applyAddToCart({ ...payload, mass: newQuantity });
            return;
          }
          handleError(
            `Value is greater than available stock of ${formatNumberDecimal(
              totalProductQuantity,
            )} ${product.unitofmass}`,
            'error',
            true,
          );
          setTextInputError(prev => ({ ...prev, [errorKey]: true }));
          return;
        }
      }

      if (newQuantity <= variantStock) {
        setTextInputError(prev => ({ ...prev, [errorKey]: false }));
        applyAddToCart({ ...payload, mass: newQuantity });
      } else {
        handleError(
          `Value is greater than available stock of ${formatNumberDecimal(
            variantStock,
          )} ${product.unitofmass}`,
          'error',
          true,
        );
        setTextInputError(prev => ({ ...prev, [errorKey]: true }));
      }
    },
    [
      product,
      cartItems,
      updatingCartitems,
      cartOrderData,
      buildVariantCartPayload,
      handleError,
      setTextInputError,
      applyAddToCart,
      applyRemoveCartItem,
    ],
  );

  const handleVariantQuantityChange = useCallback(
    (variant: any, input: string) => {
      if (!product || !variant?._id) return;

      const payload = buildVariantCartPayload(variant);
      if (!payload) return;

      const productID = product.productID;
      const variantId = variant._id;
      const errorKey = getCartIdentityKey({ productID, variantId });
      const sanitizedInput = parseInt(input.replace(/^0+(?=\d)/, '')) || 0;
      const variantStock = Number(variant.bp_variant_quantity ?? 0);
      const variantCartItem = cartItems?.find(
        (item: any) =>
          item.productID === productID && item.variantId === variantId,
      );
      const paidQty = Number(variantCartItem?.bp_order_paid_quantity ?? 0);

      if (paidQty > 0 && sanitizedInput < paidQty) {
        // Mirror product list: let the user type freely while below paid,
        // just flag the error. Restore-to-paid happens on blur.
        applySetCartQuantity({
          productID,
          variantId,
          quantity: sanitizedInput,
          product: payload,
        });
        setQuantityInputError(prev => ({ ...prev, [errorKey]: true }));
        return;
      }

      if (sanitizedInput <= 0) {
        applyRemoveCartItem({ productID, variantId });
        return;
      }

      if (
        variantCartItem &&
        Number(sanitizedInput) === Number(variantCartItem.Quantity ?? 0)
      ) {
        return;
      }

      if (updatingCartitems && cartOrderData?.order?.length) {
        const originalProduct = cartOrderData.order.find(
          (op: any) =>
            op?.bp_product_information?._id === productID &&
            (op?.bp_product_information?.bp_variant_id ??
              op?.bp_variant_id ??
              null) === variantId,
        );
        if (originalProduct?.bp_product_information) {
          const unpaidQuantity =
            Number(originalProduct?.bp_order_quantity ?? 0) -
            Number(originalProduct?.bp_order_paid_quantity ?? 0);
          const totalProductQuantity =
            variantStock +
            Number(unpaidQuantity) +
            Number(originalProduct?.bp_order_paid_quantity ?? 0);

          if (Number(sanitizedInput) <= Number(totalProductQuantity)) {
            applySetCartQuantity({
              productID,
              variantId,
              quantity: Number(sanitizedInput),
              product: payload,
            });
            setQuantityInputError(prev => ({ ...prev, [errorKey]: false }));
            return;
          }

          setQuantityInputError(prev => ({ ...prev, [errorKey]: true }));
          handleError(
            `Value is greater than available stock of ${formatNumberDecimal(
              totalProductQuantity,
            )}`,
            'error',
            true,
          );
          return;
        }
      }

      if (Number(sanitizedInput) <= variantStock) {
        applySetCartQuantity({
          productID,
          variantId,
          quantity: Number(sanitizedInput),
          product: payload,
        });
        setQuantityInputError(prev => ({ ...prev, [errorKey]: false }));
      } else {
        handleError(
          `Value is greater than available stock of ${formatNumberDecimal(
            variantStock,
          )}`,
          'error',
          true,
        );
        setQuantityInputError(prev => ({ ...prev, [errorKey]: true }));
      }
    },
    [
      product,
      cartItems,
      updatingCartitems,
      cartOrderData,
      buildVariantCartPayload,
      handleError,
      setQuantityInputError,
      applySetCartQuantity,
      applyRemoveCartItem,
    ],
  );

  // Restore-to-paid on blur (item type). Mirrors Product.web handleQuantityBlur.
  const handleQuantityBlur = useCallback(() => {
    if (!product) return;
    const productID = product.productID;
    const live = cartItems?.find(
      (item: any) =>
        item.productID === productID && (item.variantId ?? null) === null,
    );
    const paid = Number(live?.bp_order_paid_quantity ?? 0);
    if (!updatingCartitems || paid <= 0) return;
    if ((live?.Quantity ?? 0) >= paid) return;

    applySetCartQuantity({ productID, quantity: paid, product });
    setQuantityInputError(prev => ({ ...prev, [productID]: false }));
    showPaidLineCannotRemove();
  }, [product, cartItems, updatingCartitems, applySetCartQuantity, setQuantityInputError]);

  const handleVariantQuantityBlur = useCallback(
    (variant: any) => {
      if (!product || !variant?._id) return;
      const productID = product.productID;
      const variantId = variant._id;
      const errorKey = getCartIdentityKey({ productID, variantId });
      const live = cartItems?.find(
        (item: any) =>
          item.productID === productID && item.variantId === variantId,
      );
      const paid = Number(live?.bp_order_paid_quantity ?? 0);
      if (!updatingCartitems || paid <= 0) return;
      if ((live?.Quantity ?? 0) >= paid) return;

      const payload = buildVariantCartPayload(variant);
      if (!payload) return;
      applySetCartQuantity({
        productID,
        variantId,
        quantity: paid,
        product: payload,
      });
      setQuantityInputError(prev => ({ ...prev, [errorKey]: false }));
      showPaidLineCannotRemove();
    },
    [product, cartItems, updatingCartitems, buildVariantCartPayload, applySetCartQuantity, setQuantityInputError],
  );

  // Restore-to-paid on blur (unit/mass type). Mirrors Product.web handleMassBlur.
  const handleMassBlur = useCallback(() => {
    if (!product) return;
    const productID = product.productID;
    const live = cartItems?.find(
      (item: any) =>
        item.productID === productID && (item.variantId ?? null) === null,
    );
    const paid = Number(live?.bp_order_paid_mass ?? 0);
    if (!updatingCartitems || paid <= 0) return;
    const raw = massValue;
    const parsed = parseFloat(raw);
    const current =
      raw === '' || !Number.isFinite(parsed) || parsed <= 0 ? 0 : parsed;
    if (current >= paid) return;

    setMassValue(String(paid));
    applyAddToCart({ ...product, mass: paid });
    setTextInputError(prev => ({ ...prev, [productID]: false }));
    showPaidLineCannotRemove();
  }, [product, cartItems, updatingCartitems, massValue, applyAddToCart, setTextInputError]);

  const handleVariantMassBlur = useCallback(
    (variant: any) => {
      if (!product || !variant?._id) return;
      const productID = product.productID;
      const variantId = variant._id;
      const errorKey = getCartIdentityKey({ productID, variantId });
      const live = cartItems?.find(
        (item: any) =>
          item.productID === productID && item.variantId === variantId,
      );
      const paid = Number(live?.bp_order_paid_mass ?? 0);
      if (!updatingCartitems || paid <= 0) return;
      const raw = variantMassText[variantId] ?? '';
      const parsed = parseFloat(raw);
      const current =
        raw === '' || !Number.isFinite(parsed) || parsed <= 0 ? 0 : parsed;
      if (current >= paid) return;

      const payload = buildVariantCartPayload(variant);
      if (!payload) return;
      setVariantMassText(prev => ({ ...prev, [variantId]: String(paid) }));
      applyAddToCart({ ...payload, mass: paid });
      setTextInputError(prev => ({ ...prev, [errorKey]: false }));
      showPaidLineCannotRemove();
    },
    [product, cartItems, updatingCartitems, variantMassText, buildVariantCartPayload, applyAddToCart, setTextInputError],
  );

  const onAddSelectedVariant = useCallback(() => {
    const payload = buildSelectedVariantCartPayload();
    if (!payload) return;
    if (Number(payload.quantity ?? 0) <= 0) {
      AppSnackbar.show(`${payload.itemname} is out of stock`, 'warning', {
        durationMs: 4500,
      });
      return;
    }
    if (payload.price_type === 'unit') {
      const currentMass = Number(cartItem?.mass ?? 0);
      const nextMass = Math.min(
        currentMass + 1,
        Number(payload.quantity ?? currentMass + 1),
      );
      if (nextMass <= currentMass) {
        AppSnackbar.show(`${payload.itemname} is out of stock`, 'warning', {
          durationMs: 4500,
        });
        return;
      }
      applyAddToCart({ ...payload, mass: nextMass });
      return;
    }
    applyAddToCart(payload);
  }, [buildSelectedVariantCartPayload, cartItem, applyAddToCart]);

  const onSubSelectedVariant = useCallback(() => {
    const payload = buildSelectedVariantCartPayload();
    if (!payload || !cartItem) return;

    if (payload.price_type === 'unit') {
      const paidMass = Number(cartItem?.bp_order_paid_mass ?? 0);
      const currentMass = Number(cartItem?.mass ?? 0);
      const nextMass = Math.max(0, currentMass - 1);
      if (paidMass > 0 && nextMass < paidMass) {
        showPaidLineCannotRemove();
        return;
      }
      if (nextMass <= 0) {
        applyRemoveCartItem({
          productID: payload.productID,
          variantId: payload.variantId,
        });
      } else {
        applyAddToCart({ ...payload, mass: nextMass });
      }
      return;
    }

    const paidQty = Number(cartItem?.bp_order_paid_quantity ?? 0);
    const currentQty = cartItem?.Quantity ?? 0;
    if (paidQty > 0 && currentQty <= paidQty) {
      showPaidLineCannotRemove();
      return;
    }
    handleSubproductQuantity(payload);
  }, [
    buildSelectedVariantCartPayload,
    cartItem,
    applyAddToCart,
    applyRemoveCartItem,
    handleSubproductQuantity,
  ]);

  if (!product) return null;

  const handlePrimaryCta = () => {
    bottomSheetRef.current?.close();
  };

  const lineTotal =
    Number(activePrice || 0) *
    Number(
      product.price_type === 'unit'
        ? (cartItem?.mass ?? 0)
        : (cartItem?.Quantity ?? 0),
    ) || 0;

  const renderQuantityControl = () => {
    if (!product) return null;

    if (product.price_type === 'unit') {
      return (
        <View style={styles.quantitySection}>
          <View style={styles.quantityHeader}>
            <MaterialCommunityIcons
              name="scale"
              size={iconSize(20)}
              color={theme.colors.primary}
            />
            <Text style={styles.quantityLabel}>Enter Weight</Text>
          </View>
          <View
            style={[
              styles.weightInputContainer,
              textInputError[`${product.productID}::`] && styles.inputError,
            ]}>
            <BottomSheetTextInput
              style={styles.weightInput}
              keyboardType="decimal-pad"
              maxLength={7}
              placeholderTextColor={theme.colors.textTertiary}
              placeholder="0.00"
              value={massValue}
              onChangeText={handleMassInputChange}
              onFocus={handleInputFocus}
              onBlur={() => {
                handleInputBlur();
                handleMassBlur();
              }}
              editable={product.quantity > 0}
            />
            <View style={styles.unitBadge}>
              <Text style={styles.unitBadgeText}>{product.unitofmass}</Text>
            </View>
          </View>
        </View>
      );
    }

    return (
      <View style={styles.quantitySection}>
        <View style={styles.quantityHeader}>
          <MaterialCommunityIcons
            name="package-variant"
            size={iconSize(20)}
            color={theme.colors.primary}
          />
          <Text style={styles.quantityLabel}>Quantity</Text>
        </View>
        <View style={styles.stepperContainer}>
          <TouchableOpacity
            style={[
              styles.stepperButton,
              styles.stepperButtonMinus,
              !cartItem && styles.stepperButtonDisabled,
            ]}
            onPress={onSub}
            disabled={!cartItem}
            activeOpacity={0.7}>
            <Ionicons
              name="remove"
              size={iconSize(24)}
              color={cartItem ? theme.colors.error : theme.colors.textTertiary}
            />
          </TouchableOpacity>

          <View style={styles.stepperInputWrapper}>
            <BottomSheetTextInput
              style={[
                styles.stepperInput,
                quantityInputError[`${product?.productID}::`] && styles.inputError,
              ]}
              keyboardType="numeric"
              inputMode="numeric"
              maxLength={5}
              placeholderTextColor={theme.colors.textTertiary}
              placeholder="0"
              value={cartItem ? String(cartItem.Quantity ?? '') : ''}
              onChangeText={handleQuantityInputChange}
              onFocus={handleInputFocus}
              onBlur={() => {
                handleInputBlur();
                handleQuantityBlur();
              }}
              editable={product.quantity > 0}
            />
          </View>

          <TouchableOpacity
            style={[
              styles.stepperButton,
              styles.stepperButtonPlus,
              product.quantity <= 0 && styles.stepperButtonDisabled,
            ]}
            onPress={() => {
              if (product.quantity <= 0) {
                AppSnackbar.show(
                  `${product.itemname} is out of stock`,
                  'warning',
                  { durationMs: 4500 },
                );
                return;
              }
              onAdd();
            }}
            disabled={product.quantity <= 0}
            activeOpacity={0.7}>
            <Ionicons
              name="add"
              size={iconSize(24)}
              color={
                product.quantity > 0
                  ? theme.colors.success
                  : theme.colors.textTertiary
              }
            />
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  return (
    <CustomBottomSheet
      bottomSheetModalRef={bottomSheetRef}
      snapPoints={snapPoints}
      enableDynamicSizing={false}
      handleSheetChanges={handleSheetChanges}
      constrainWidth
      // footer={
      //   <View style={styles.footerContainer}>
      //     <View style={styles.footerContent}>
      //       <View style={styles.footerTotalSection}>
      //         <Text style={styles.footerTotalLabel}>Line Total</Text>
      //         <Text style={styles.footerTotalValue}>
      //           {currencyText}{' '}
      //           {formatWithCommas(lineTotal, lineTotal < 1 ? 3 : 0)}
      //         </Text>
      //       </View>
      //       <Button
      //         backgroundColor={theme.colors.primary}
      //         rippleColor={theme.colors.primaryDark}
      //         text="Done"
      //         paddingHorizontal={s(32)}
      //         borderRadius={theme.borderRadius.lg}
      //         height={v(48)}
      //         fontSize={16}
      //         onPress={handlePrimaryCta}
      //       />
      //     </View>
      //   </View>
      // }
      disableDefaultScroll>
      <BottomSheetScrollView
        contentContainerStyle={styles.scrollContent}
        bounces={false}
        overScrollMode="never"
        keyboardShouldPersistTaps="handled">
        {/* Product Hero Card */}
        <View style={styles.heroCard}>
          <View style={styles.heroImageContainer}>
            {activeImage ? (
              <Image
                source={{ uri: forceHttps(activeImage) }}
                style={styles.heroImage}
                resizeMode="cover"
              />
            ) : (
              <View style={styles.heroImagePlaceholder}>
                <FontAwesome6
                  name="image"
                  size={iconSize(48)}
                  color={theme.colors.textSecondary}
                />
              </View>
            )}
          </View>

          <View style={styles.heroInfo}>
            <View style={styles.productNameContainer}>
              <Text style={styles.productName} numberOfLines={2}>
                {productName.trim()}
              </Text>
              {!cartBridge && (isOwner || !product.image) && (
                <TouchableOpacity
                  onPress={handleProductImageEditPress}
                  activeOpacity={0.7}
                  style={styles.imageEditButton}
                  accessibilityRole="button"
                  accessibilityLabel={
                    isOwner ? 'Edit product' : 'Add image for product'
                  }>
                  <Text style={styles.imageEditCta}>
                    {isOwner ? 'Edit Product' : 'Add Image'}
                  </Text>
                </TouchableOpacity>
              )}
            </View>

            <View style={styles.priceRow}>
              <Text style={styles.priceValue}>
                {currencyText}{' '}
                {activePrice
                  ? product.price_type === 'unit'
                    ? `${formatWithCommas(activePrice, activePrice < 1 ? 3 : 0)}`
                    : formatWithCommas(activePrice, activePrice < 1 ? 3 : 0)
                  : '0'}
              </Text>
              {product.price_type === 'unit' && (
                <Text style={styles.priceUnit}>/ {product.unitofmass}</Text>
              )}
            </View>

            {stockStatus ? (
              <View
                style={[
                  styles.stockStatusPill,
                  { backgroundColor: stockStatus.backgroundColor },
                ]}
                accessibilityRole="text"
                accessibilityLabel={stockStatus.label}>
                <Ionicons
                  name={stockStatus.icon}
                  size={iconSize(14)}
                  color={stockStatus.color}
                />
                <Text
                  style={[
                    styles.stockStatusPillText,
                    { color: stockStatus.color },
                  ]}>
                  {stockStatus.label}
                </Text>
              </View>
            ) : null}

            <View style={styles.categoryRow}>
              <View style={styles.categoryBadge}>
                <Text style={styles.categoryText}>{product.category}</Text>
              </View>
              {!cartBridge ? (
                <Tooltip
                  title={
                    isFavourite ? 'Remove from favourites' : 'Add to favourites'
                  }>
                  <TouchableOpacity
                    onPress={onToggleFavourite}
                    disabled={isFavLoading}
                    style={styles.favouriteButton}
                    activeOpacity={0.7}>
                    <Ionicons
                      name={isFavourite ? 'heart' : 'heart-outline'}
                      size={iconSize(24)}
                      color={
                        isFavourite
                          ? theme.colors.error
                          : theme.colors.textSecondary
                      }
                    />
                  </TouchableOpacity>
                </Tooltip>
              ) : null}
            </View>
          </View>
        </View>

        {hasVariants ? (
          <View style={styles.variantCard}>
            <Text style={styles.sectionTitle}>Select Variant</Text>
            <View style={styles.variantList}>
              {variants.map((variant: any) => {
                const isSelected = variant?._id === selectedVariant?._id;
                const variantQuantity = Number(variant?.bp_variant_quantity ?? 0);
                const variantPrice = Number(variant?.bp_variant_selling_price ?? 0);
                const variantMsl = Number(variant?.bp_variant_minimum_stock_level ?? 0);
                const isVariantOutOfStock = variantQuantity <= 0;
                const isVariantLowStock =
                  !isVariantOutOfStock &&
                  variantMsl > 0 &&
                  variantQuantity <= variantMsl;
                const variantStockColor =
                  isVariantOutOfStock || isVariantLowStock
                    ? theme.colors.error
                    : theme.colors.textSecondary;
                const variantAttributes = Object.values(
                  variant?.bp_variant_attributes || {},
                )
                  .filter(Boolean)
                  .join(' / ');
                const variantLabel = composeProductVariantLabel(
                  product.itemname,
                  variant?.bp_variant_display_name || variantAttributes || '',
                );
                const variantCartItem = cartItems?.find(
                  (item: any) =>
                    item.productID === product.productID &&
                    item.variantId === variant?._id,
                );
                const variantIdentityKey = getCartIdentityKey({
                  productID: product.productID,
                  variantId: variant?._id ?? null,
                });
                const previousOrderVariantLine = pendingPreviousOrder?.find(
                  (item: any) => getCartIdentityKey(item) === variantIdentityKey,
                );
                const partialLineItem = variantCartItem || previousOrderVariantLine;
                const shouldShowPartialInfo = Boolean(
                  currentOrderType === 'partial' &&
                  originalOrderProductIdSet.has(variantIdentityKey) &&
                  partialLineItem,
                );
                const qtyInCart = variantCartItem?.Quantity ?? 0;
                const unpaidMass =
                  Number(partialLineItem?.mass ?? 0) -
                  Number(partialLineItem?.bp_order_paid_mass ?? 0);
                const unpaidQuantity =
                  Number(partialLineItem?.Quantity ?? 0) -
                  Number(partialLineItem?.bp_order_paid_quantity ?? 0);
                const isFullyPaidPartial =
                  !!partialLineItem?.bp_order_completed &&
                  (partialLineItem?.price_type === 'unit'
                    ? unpaidMass <= 0 || isNumericEffectivelyZero(unpaidMass)
                    : isNumericEffectivelyZero(unpaidQuantity));
                const partialStatusLabel = isFullyPaidPartial
                  ? partialLineItem?.price_type === 'unit'
                    ? `${formatNumberDecimal(
                        partialLineItem?.bp_order_paid_mass ?? 0,
                      )} ${product.unitofmass || ''} PAID`
                    : `${formatNumberDecimal(
                        partialLineItem?.bp_order_paid_quantity ??
                          partialLineItem?.Quantity ??
                          0,
                      )} PAID`
                  : partialLineItem?.price_type === 'unit'
                    ? `${formatNumberDecimal(unpaidMass)} ${product.unitofmass || ''} UNPAID`
                    : `${formatNumberDecimal(unpaidQuantity)} UNPAID`;
                return (
                  <TouchableOpacity
                    key={variant?._id}
                    onPress={() => setSelectedVariantId(variant?._id)}
                    activeOpacity={0.8}
                    style={[
                      styles.variantItem,
                      isSelected && styles.variantItemSelected,
                    ]}>
                    <View style={styles.variantItemInfo}>
                      <Text
                        numberOfLines={2}
                        style={[
                          styles.variantItemName,
                          isSelected && styles.variantItemNameSelected,
                        ]}>
                        {variantLabel}
                      </Text>
                      <Text style={styles.variantItemMeta}>
                        {currencyText}{' '}
                        {formatWithCommas(variantPrice, variantPrice < 1 ? 3 : 0)}{' '}
                        • Stock:{' '}
                        <Text style={{ color: variantStockColor }}>
                          {formatNumberDecimal(variantQuantity)}
                        </Text>
                      </Text>
                      {(() => {
                        if (isVariantOutOfStock) {
                          return (
                            <View style={[styles.variantStockChip, { backgroundColor: theme.colors.errorLight }]}>
                              <Text style={[styles.variantStockChipText, { color: theme.colors.error }]}>
                                Out of stock
                              </Text>
                            </View>
                          );
                        }
                        if (isVariantLowStock) {
                          return (
                            <View style={[styles.variantStockChip, { backgroundColor: `${theme.colors.warningDark}1A` }]}>
                              <Text style={[styles.variantStockChipText, { color: theme.colors.warningDark }]}>
                                Low stock
                              </Text>
                            </View>
                          );
                        }
                        return null;
                      })()}
                      {shouldShowPartialInfo ? (
                        <View
                          style={
                            isFullyPaidPartial
                              ? styles.paidPill
                              : styles.unpaidPill
                          }>
                          <Text
                            style={
                              isFullyPaidPartial
                                ? styles.paidPillText
                                : styles.unpaidPillText
                            }
                            numberOfLines={1}>
                            {partialStatusLabel}
                          </Text>
                        </View>
                      ) : null}
                    </View>
                    <View style={styles.variantItemControls}>
                      <QuantityControls
                        priceType={product.price_type === 'unit' ? 'unit' : 'item'}
                        quantity={qtyInCart}
                        mass={variantCartItem?.mass ?? 0}
                        massText={
                          product.price_type === 'unit'
                            ? variantMassText[variant?._id] !== undefined
                              ? variantMassText[variant._id]
                              : variantCartItem?.mass
                                ? String(variantCartItem.mass)
                                : ''
                            : undefined
                        }
                        unitOfMass={product.unitofmass || ''}
                        onAdd={() => onAddVariant(variant)}
                        onSubtract={() => onSubVariant(variant)}
                        onQuantityChange={
                          product.price_type !== 'unit'
                            ? (input: string) =>
                                handleVariantQuantityChange(variant, input)
                            : () => { }
                        }
                        onMassChange={
                          product.price_type === 'unit'
                            ? (input: string) =>
                                handleVariantMassChange(variant, input)
                            : undefined
                        }
                        onMassBlur={
                          product.price_type === 'unit'
                            ? () => {
                                handleInputBlur();
                                handleVariantMassBlur(variant);
                              }
                            : undefined
                        }
                        onQuantityBlur={
                          product.price_type !== 'unit'
                            ? () => {
                                handleInputBlur();
                                handleVariantQuantityBlur(variant);
                              }
                            : undefined
                        }
                        onInputFocus={handleInputFocus}
                        useBottomSheetInput
                        hasError={
                          product.price_type === 'unit'
                            ? !!textInputError[variantIdentityKey]
                            : !!quantityInputError[variantIdentityKey]
                        }
                        hideSubtractWhenZero
                        showPlusDisabledStyle={variantQuantity <= 0}
                        fixedHeight
                      />
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        ) : null}

        {/* Product Attributes */}
        {isReady && (product.size || product.model) && (
          <View style={styles.attributesCard}>
            <Text style={styles.sectionTitle}>Specifications</Text>
            <View style={styles.attributesGrid}>
              {product.size && (
                <View style={styles.attributeItem}>
                  <MaterialCommunityIcons
                    name="ruler"
                    size={iconSize(18)}
                    color={theme.colors.textSecondary}
                  />
                  <View style={styles.attributeContent}>
                    <Text style={styles.attributeLabel}>Size</Text>
                    <Text style={styles.attributeValue}>{product.size}</Text>
                  </View>
                </View>
              )}
              {product.model && (
                <View style={styles.attributeItem}>
                  <MaterialCommunityIcons
                    name="cube-outline"
                    size={iconSize(18)}
                    color={theme.colors.textSecondary}
                  />
                  <View style={styles.attributeContent}>
                    <Text style={styles.attributeLabel}>Model</Text>
                    <Text style={styles.attributeValue}>{product.model}</Text>
                  </View>
                </View>
              )}
            </View>
          </View>
        )}

        {/* Stock Info Card */}
        <View style={styles.stockCard}>
          <View style={styles.stockCardHeader}>
            <MaterialCommunityIcons
              name="warehouse"
              size={iconSize(20)}
              color={theme.colors.textSecondary}
            />
            <Text style={styles.stockCardTitle}>Inventory</Text>
          </View>
          <View style={styles.stockCardContent}>
            <View style={styles.stockInfoRow}>
              <Text style={styles.stockInfoLabel}>Available Stock</Text>
              <Text
                style={[
                  styles.stockInfoValue,
                  activeQuantity <= activeMinimumStock && {
                    color: theme.colors.error,
                  },
                ]}>
                {product.price_type === 'unit'
                  ? `${formatNumberDecimal(activeQuantity)} ${product.unitofmass}`
                  : formatNumberDecimal(activeQuantity)}
              </Text>
            </View>
            {activeMinimumStock > 0 && (
              <View style={styles.stockInfoRow}>
                <Text style={styles.stockInfoLabel}>Minimum Stock Level</Text>
                <Text style={styles.stockInfoValue}>
                  {formatNumberDecimal(activeMinimumStock)}
                </Text>
              </View>
            )}
          </View>
        </View>

        {/* Quantity Controls — non-variant only; variants use per-row QuantityControls */}
        {!hasVariants ? (
          <View style={styles.controlsCard}>{renderQuantityControl()}</View>
        ) : null}

        {/* Spacer for footer */}
        <View style={{ height: v(100) }} />
      </BottomSheetScrollView>
    </CustomBottomSheet>
  );
};

export default React.memo(ProductDetailBottomSheet);

const createStyles = (theme: any) =>
  StyleSheet.create({
    scrollContent: {
      paddingHorizontal: s(16),
      paddingTop: v(8),
    },
    headerContainer: {
      paddingTop: v(8),
      paddingBottom: v(4),
    },
    headerTitleRow: {
      alignItems: 'center',
    },
    headerDragIndicator: {
      width: s(40),
      height: v(4),
      backgroundColor: theme.colors.border,
      borderRadius: theme.borderRadius.round,
    },

    // Hero Card
    heroCard: {
      flexDirection: 'row',
      backgroundColor: theme.colors.surface,
      borderRadius: isTablet ? theme.borderRadius.sm : theme.borderRadius.md,
      padding: s(12),
      marginBottom: v(12),
      ...Platform.select({
        ios: {
          shadowColor: theme.colors.shadow,
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.08,
          shadowRadius: 8,
        },
        android: {
          elevation: 1.5,
        },
        web: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },
      }),
    },
    heroImageContainer: {
      width: s(isTablet ? 140 : 110),
      height: s(isTablet ? 140 : 110),
      borderRadius: theme.borderRadius.md,
      overflow: 'hidden',
      backgroundColor: theme.colors.backgroundGray100,
    },
    imageEditButton: {
      padding: s(4),
      flexShrink: 0,
      marginTop: v(4),
    },
    imageEditCta: {
      fontSize: fs(12),
      fontFamily: GTFonts.semibold,
      color: theme.colors.success,
      lineHeight: fs(16),
      textDecorationLine: 'underline',
    },
    heroImage: {
      width: '100%',
      height: '100%',
    },
    heroImagePlaceholder: {
      width: '100%',
      height: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.backgroundGray100,
    },
    stockStatusPill: {
      alignSelf: 'flex-start',
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(4),
      marginTop: v(6),
      paddingHorizontal: s(8),
      paddingVertical: s(4),
      borderRadius: theme.borderRadius.sm,
    },
    stockStatusPillText: {
      fontSize: fs(12),
      fontFamily: GTFonts.semibold,
      lineHeight: fs(14),
    },
    heroInfo: {
      flex: 1,
      minWidth: 0,
      marginLeft: s(12),
      justifyContent: 'space-between',
    },
    favouriteButton: {
      padding: s(4),
    },
    productNameContainer: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: s(8),
    },
    productName: {
      flex: 1,
      flexShrink: 1,
      fontSize: fs(isTablet ? 18 : 16),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
      lineHeight: fs(isTablet ? 22 : 20),
      marginTop: v(4),
    },
    priceRow: {
      flexDirection: 'row',
      alignItems: 'baseline',
      marginTop: v(4),
    },
    priceValue: {
      fontSize: fs(isTablet ? 20 : 18),
      fontFamily: GTFonts.bold,
      color: theme.colors.textDark || theme.colors.text,
      lineHeight: fs(isTablet ? 24 : 22),
    },
    priceUnit: {
      fontSize: fs(13),
      fontFamily: GTFonts.regular,
      color: theme.colors.textSecondary,
      marginLeft: s(2),
    },
    categoryRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: v(6),
    },
    categoryBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(4),
      flex: 1,
    },
    categoryText: {
      fontSize: fs(12),
      fontFamily: GTFonts.regular,
      color: theme.colors.textSecondary,
      lineHeight: fs(14),
    },

    // Attributes Card
    variantCard: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      padding: s(14),
      marginBottom: v(12),
      ...Platform.select({
        ios: {
          shadowColor: theme.colors.shadow,
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.06,
          shadowRadius: 4,
        },
        android: {
          elevation: 2,
        },
        web: {
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        },
      }),
    },
    variantList: {
      gap: v(8),
    },
    variantItem: {
      borderWidth: borderWidth(2),
      borderColor: theme.colors.border,
      borderRadius: theme.borderRadius.md,
      paddingHorizontal: s(10),
      paddingVertical: v(10),
      backgroundColor: theme.colors.background,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    variantItemSelected: {
      borderColor: theme.colors.borderGray,
    },
    variantItemInfo: {
      flexDirection: 'column',
      gap: v(2),
      flex: 1,
    },
    variantItemName: {
      fontSize: fs(13),
      lineHeight: fs(16),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
    },
    variantItemNameSelected: {
      // color: theme.colors.primary,
    },
    variantItemMeta: {
      fontSize: fs(12),
      lineHeight: fs(15),
      fontFamily: GTFonts.regular,
      color: theme.colors.textSecondary,
    },
    variantStockChip: {
      alignSelf: 'flex-start',
      paddingHorizontal: s(6),
      paddingVertical: s(2),
      borderRadius: theme.borderRadius.sm,
      marginTop: v(3),
    },
    variantStockChipText: {
      fontSize: fs(10),
      fontFamily: GTFonts.semibold,
      lineHeight: fs(12),
    },
    paidPill: {
      alignSelf: 'flex-start',
      marginTop: v(4),
      backgroundColor: `${theme.colors.success}18`,
      borderRadius: theme.borderRadius.sm,
      paddingHorizontal: s(6),
      paddingVertical: v(2),
    },
    paidPillText: {
      fontSize: fs(10),
      lineHeight: fs(13),
      fontFamily: GTFonts.semibold,
      color: theme.colors.success,
    },
    unpaidPill: {
      alignSelf: 'flex-start',
      marginTop: v(4),
      backgroundColor: `${theme.colors.error}18`,
      borderRadius: theme.borderRadius.sm,
      paddingHorizontal: s(6),
      paddingVertical: v(2),
    },
    unpaidPillText: {
      fontSize: fs(10),
      lineHeight: fs(13),
      fontFamily: GTFonts.semibold,
      color: theme.colors.error,
    },
    variantItemControls: {
      marginLeft: s(8),
    },
    variantActionsRow: {
      marginTop: v(12),
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: s(10),
    },
    variantActionButton: {
      width: s(40),
      height: s(40),
      borderRadius: s(20),
      borderWidth: borderWidth(1.5),
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: theme.colors.background,
      borderColor: theme.colors.border,
    },
    variantActionButtonPrimary: {
      borderColor: theme.colors.success,
      backgroundColor: theme.colors.successLight,
    },
    variantCartQtyText: {
      flex: 1,
      textAlign: 'center',
      fontSize: fs(14),
      lineHeight: fs(18),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
    },

    // Attributes Card
    attributesCard: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.lg,
      padding: s(14),
      marginBottom: v(12),
      ...Platform.select({
        ios: {
          shadowColor: theme.colors.shadow,
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.06,
          shadowRadius: 4,
        },
        android: {
          elevation: 2,
        },
        web: {
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        },
      }),
    },
    sectionTitle: {
      fontSize: fs(14),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
      marginBottom: v(10),
      lineHeight: fs(16),
    },
    attributesGrid: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: s(12),
    },
    attributeItem: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
      backgroundColor: theme.colors.background,
      paddingHorizontal: s(10),
      paddingVertical: s(8),
      borderRadius: theme.borderRadius.sm,
      minWidth: '45%',
    },
    attributeContent: {
      flex: 1,
    },
    attributeLabel: {
      fontSize: fs(11),
      fontFamily: GTFonts.regular,
      color: theme.colors.textSecondary,
      lineHeight: fs(13),
    },
    attributeValue: {
      fontSize: fs(13),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
      lineHeight: fs(16),
    },

    // Stock Card (match hero / product card radius + elevation)
    stockCard: {
      backgroundColor: theme.colors.surface,
      borderRadius: isTablet ? theme.borderRadius.sm : theme.borderRadius.md,
      padding: s(14),
      marginBottom: v(12),
      ...Platform.select({
        ios: {
          shadowColor: theme.colors.shadow,
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.08,
          shadowRadius: 8,
        },
        android: {
          elevation: 1.5,
        },
        web: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        },
      }),
    },
    stockCardHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
      marginBottom: v(10),
    },
    stockCardTitle: {
      fontSize: fs(14),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
      lineHeight: fs(16),
    },
    stockCardContent: {
      gap: v(8),
    },
    stockInfoRow: {
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingVertical: v(4),
    },
    stockInfoLabel: {
      fontSize: fs(13),
      fontFamily: GTFonts.regular,
      color: theme.colors.text,
      lineHeight: fs(16),
    },
    stockInfoValue: {
      fontSize: fs(14),
      fontFamily: GTFonts.semibold,
      color: theme.colors.success,
      lineHeight: fs(17),
    },

    // Controls Card
    controlsCard: {
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      padding: s(16),
      marginBottom: v(12),
      ...Platform.select({
        ios: {
          shadowColor: theme.colors.shadow,
          shadowOffset: { width: 0, height: 1 },
          shadowOpacity: 0.06,
          shadowRadius: 4,
        },
        android: {
          elevation: 2,
        },
        web: {
          boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
        },
      }),
    },
    quantitySection: {},
    quantityHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: s(8),
      marginBottom: v(12),
    },
    quantityLabel: {
      fontSize: fs(15),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
      lineHeight: fs(18),
    },

    // Weight Input (unit products)
    weightInputContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      backgroundColor: theme.colors.background,
      borderRadius: theme.borderRadius.md,
      borderWidth: borderWidth(1),
      borderColor: theme.colors.border,
      overflow: 'hidden',
    },
    weightInput: {
      flex: 1,
      fontSize: fs(isTablet ? 22 : 20),
      fontFamily: GTFonts.semibold,
      color: theme.colors.text,
      paddingHorizontal: s(16),
      paddingVertical: v(14),
      textAlign: 'center',
    },
    unitBadge: {
      backgroundColor: theme.colors.primary,
      paddingHorizontal: s(16),
      paddingVertical: v(14),
      justifyContent: 'center',
      alignItems: 'center',
      minWidth: s(60),
    },
    unitBadgeText: {
      color: '#fff',
      fontSize: fs(14),
      fontFamily: GTFonts.semibold,
      lineHeight: fs(16),
    },

    // Stepper (item products)
    stepperContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: s(12),
    },
    stepperButton: {
      width: s(48),
      height: s(48),
      borderRadius: s(24),
      alignItems: 'center',
      justifyContent: 'center',
      borderWidth: borderWidth(1.5),
    },
    stepperButtonPlus: {
      backgroundColor: theme.colors.successLight,
      borderColor: theme.colors.success,
    },
    stepperButtonMinus: {
      backgroundColor: theme.colors.errorLight,
      borderColor: theme.colors.error,
    },
    stepperButtonDisabled: {
      backgroundColor: theme.colors.background,
      borderColor: theme.colors.border,
    },
    stepperInputWrapper: {
      flex: 1,
      maxWidth: s(120),
    },
    stepperInput: {
      backgroundColor: theme.colors.background,
      borderRadius: theme.borderRadius.md,
      borderWidth: borderWidth(1),
      borderColor: theme.colors.border,
      fontSize: fs(isTablet ? 24 : 22),
      fontFamily: GTFonts.bold,
      color: theme.colors.text,
      paddingHorizontal: s(12),
      paddingVertical: v(12),
      textAlign: 'center',
    },
    inputError: {
      borderColor: theme.colors.error,
      borderWidth: borderWidth(1.5),
    },

    // Footer
    footerContainer: {
      backgroundColor: theme.colors.surface,
      borderTopWidth: borderWidth(1),
      borderTopColor: theme.colors.border,
      paddingTop: v(12),
      paddingHorizontal: s(16),
    },
    footerContent: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    footerTotalSection: {
      flex: 1,
    },
    footerTotalLabel: {
      fontSize: fs(12),
      fontFamily: GTFonts.regular,
      color: theme.colors.textSecondary,
      lineHeight: fs(14),
    },
    footerTotalValue: {
      fontSize: fs(isTablet ? 22 : 20),
      fontFamily: GTFonts.bold,
      color: theme.colors.text,
      lineHeight: fs(isTablet ? 26 : 24),
    },
  });
